/**
 * Formatting for the NAERAS dashboard, as the client's chart document fixes
 * it: Greek separators (18,44 and 67.079), MWh and metres with 2 decimals, m³
 * with none, and "—" for anything missing — never a 0 that wasn't measured.
 */

const ATHENS_TZ = 'Europe/Athens';

const formatters: Record<number, Intl.NumberFormat> = {};

function formatter(decimals: number): Intl.NumberFormat {
  formatters[decimals] ??= new Intl.NumberFormat('el-GR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });

  return formatters[decimals];
}

export const MISSING = '—';

export function isNum(value: number | null | undefined): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

/** A number in Greek format, or "—". */
export function num(value: number | null | undefined, decimals = 2): string {
  return isNum(value) ? formatter(decimals).format(value) : MISSING;
}

export const mwh = (value: number | null | undefined) =>
  isNum(value) ? `${num(value, 2)} MWh` : MISSING;

export const m3 = (value: number | null | undefined) =>
  isNum(value) ? `${num(value, 0)} m³` : MISSING;

export const metres = (value: number | null | undefined) =>
  isNum(value) ? `${num(value, 2)} m` : MISSING;

/** A signed difference, "+1,20" / "−0,24", or "—". */
export function signed(value: number | null | undefined, decimals = 2): string {
  if (!isNum(value)) return MISSING;

  const sign = value > 0 ? '+' : value < 0 ? '−' : '';

  return `${sign}${num(Math.abs(value), decimals)}`;
}

/** A share as "63,1%", or "—" when either side is missing or the base is 0. */
export function pct(part: number | null | undefined, whole: number | null | undefined): string {
  if (!isNum(part) || !isNum(whole) || whole === 0) return MISSING;

  return `${num((part / whole) * 100, 1)}%`;
}

/** "14:00" for hour 14. */
export const hourLabel = (hour: number) => `${String(hour).padStart(2, '0')}:00`;

/** "09:12", the given instant in Greek time. */
export function athensHourMinute(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return MISSING;

  return new Intl.DateTimeFormat('el-GR', {
    timeZone: ATHENS_TZ,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(date);
}

/** "6/10" for 2026-10-06. */
export function shortDate(date: string): string {
  const [, month, day] = date.split('-');

  return `${Number(day)}/${Number(month)}`;
}

/**
 * The dashboard's palette. Hybrid is green and hydro blue in every chart;
 * anything else must be neither, and estimates must look unlike measurements.
 */
export const COLORS = {
  hybrid: '#10b981',
  hybridEst: '#6ee7b7',
  hydro: '#3b82f6',
  hydroEst: '#93c5fd',
  neutral: '#64748b',
  neutralEst: '#cbd5e1',
  other: '#f59e0b',
} as const;

/**
 * A production ratio as "1/x", with x = 1 / ratio to one decimal and no
 * trailing ",0": 0.376 → "1/2,7", 0.333 → "1/3". 0 → "0", missing → "—".
 */
export function ratioLabel(ratio: number | null | undefined): string {
  if (!isNum(ratio)) return MISSING;
  if (ratio === 0) return '0';

  const x = Math.round((1 / ratio) * 10) / 10;

  return `1/${new Intl.NumberFormat('el-GR', { maximumFractionDigits: 1 }).format(x)}`;
}
